import { Suspense, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes } from 'react-router-dom'
import {
    Applications,
    RoutePaths,
    UserPermissions,
} from '../../../constants/constants'
import { useAppSelector } from '../../../redux/hook'
import initialRoutes, { RoutesType } from '../../../routes/routes'
import NotFoundPage from '../../Miscellaneous/NotFoundPage'
import AuthenticatedLayout from '../AuthenticatedLayout'
import UnAuthenticatedLayout from '../UnAuthenticatedLayout'
import Home from './Home'
import { usePermissions } from '../../../hooks/usePermissions'

const RoutesComponent: React.FC = memo(() => {
    const { t } = useTranslation()
    const permissions = useAppSelector((state) => state.org.permissions)
    const enabledApplications = useAppSelector(
        (state) => state.org.enabledApplications,
    )
    const { hasPermission: hasPermissioncheck } = usePermissions()
    const hasApplicationsAccess = useMemo(
        () =>
            hasPermissioncheck([
                UserPermissions.ORG_ADMIN,
                UserPermissions.SYSTEM_READER,
            ]),
        [hasPermissioncheck],
    )
    const routes = useMemo(() => {
        if (!permissions?.length) {
            return initialRoutes
        }

        return initialRoutes.map((route: RoutesType) => {
            let hasPermission: boolean

            if (route.path === RoutePaths.facilityMonitoring) {
                hasPermission =
                    enabledApplications.includes(
                        Applications.facilityMonitoring.id,
                    ) || hasApplicationsAccess
            } else if (route.path === RoutePaths.ccl) {
                hasPermission =
                    enabledApplications.includes(Applications.inTransit.id) ||
                    hasApplicationsAccess
            } else if (route.path === RoutePaths.analytics) {
                hasPermission =
                    (enabledApplications.includes(Applications.analytics.id) &&
                        hasPermissioncheck(UserPermissions.ENTITY_ANALYST)) ||
                    hasApplicationsAccess
            } else if (route.path === RoutePaths.deviceManager) {
                hasPermission =
                    (enabledApplications.includes(
                        Applications.deviceManager.id,
                    ) &&
                        (hasPermissioncheck(UserPermissions.USER_ADMIN) ||
                            hasPermissioncheck(
                                UserPermissions.DEVICE_WRITER,
                            ))) ||
                    hasApplicationsAccess
            } else {
                hasPermission = route.permissions?.some((p) =>
                    permissions.includes(p),
                )
            }

            return { ...route, hasPermission }
        })
    }, [
        permissions,
        enabledApplications,
        hasApplicationsAccess,
        hasPermissioncheck,
    ])

    return (
        <Suspense fallback={<div>{t('loadingmap')}</div>}>
            <Routes>
                {routes.map((route) => {
                    // Handle redirects
                    if (route.redirectPath) {
                        return (
                            <Route
                                key={route.path}
                                path={route.path}
                                element={
                                    <Navigate
                                        to={route.redirectPath}
                                        replace={true}
                                    />
                                }
                            />
                        )
                    }

                    const RouteComponent = route.hasPermission
                        ? route.component
                        : Home

                    // Wrap in AuthenticatedLayout for protected routes
                    const WrappedComponent = route.requiredAuthGuard ? (
                        <AuthenticatedLayout>
                            <RouteComponent />
                        </AuthenticatedLayout>
                    ) : (
                        <UnAuthenticatedLayout>
                            <RouteComponent />
                        </UnAuthenticatedLayout>
                    )

                    return (
                        <Route
                            key={route.path}
                            path={route.path}
                            element={WrappedComponent}
                        />
                    )
                })}
                <Route
                    path="*"
                    element={
                        <UnAuthenticatedLayout>
                            <NotFoundPage />
                        </UnAuthenticatedLayout>
                    }
                />
            </Routes>
        </Suspense>
    )
})

RoutesComponent.displayName = 'RoutesComponent'

export default RoutesComponent
